import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $userCardsStore } from './userCardsStore';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import {
  RequestStrategyEnum,
  UsersApi,
  type DeleteUserCardsRequest,
  type UpdatePasswordRequest,
  type UserResponse,
  type UpdateUserRequest,
  type WithSignal,
} from '@shared/api';

const getUserEffect = createEffect(({ signal, ...request }: WithSignal<{ id: string }>) => UsersApi.getUser(request.id, signal));
const updateUserEffect = createEffect((request: UpdateUserRequest) => UsersApi.updateUser(request));
const changePasswordEffect = createEffect((request: UpdatePasswordRequest) => UsersApi.updatePassword(request));
const lockUserEffect = createEffect((id: string) => UsersApi.lockUser(id));
const unlockUserEffect = createEffect((id: string) => UsersApi.unlockUser(id));
const deleteUserEffect = createEffect((id: string) => UsersApi.deleteUser(id));
const deleteCardEffect = createEffect(async (params: { request: DeleteUserCardsRequest }) => {
  await UsersApi.deleteUserCards(params.request);
  return { AssignedUserId: params.request.userIds };
});
const clearEvent = createEvent();

sample({ source: deleteCardEffect.doneData, target: $userCardsStore.events.getUserCards });

const $dataStore = createStore<UserResponse | null>(null)
  .on(getUserEffect.done, (current, data) => data.result.data || current)
  .on(updateUserEffect.done, (current, data) => {
    if (!current || !data.result.data) return current;
    return { ...current, ...data.result.data };
  })
  .on(lockUserEffect.done, (current) => (current ? { ...current, locked: true } : current))
  .on(unlockUserEffect.done, (current) => (current ? { ...current, locked: false } : current))
  .on(deleteUserEffect.done, () => null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserEffect.pending, (_, data) => data)
  .on(getUserEffect.fail, () => false)
  .on(getUserEffect.done, () => false)
  .reset(clearEvent);

const $actionLoadingStore = createStore<boolean>(false)
  .on(updateUserEffect.pending, (_, data) => data)
  .on(updateUserEffect.fail, () => false)
  .on(updateUserEffect.done, () => false)
  .on(changePasswordEffect.pending, (_, data) => data)
  .on(changePasswordEffect.fail, () => false)
  .on(changePasswordEffect.done, () => false)
  .on(lockUserEffect.pending, (_, data) => data)
  .on(lockUserEffect.fail, () => false)
  .on(lockUserEffect.done, () => false)
  .on(unlockUserEffect.pending, (_, data) => data)
  .on(unlockUserEffect.fail, () => false)
  .on(unlockUserEffect.done, () => false)
  .on(deleteUserEffect.pending, (_, data) => data)
  .on(deleteUserEffect.fail, () => false)
  .on(deleteUserEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserEffect, () => null)
  .on(getUserEffect.fail, (_, data) => {
    if (data.error.message === 'Action is forbidden.') return 'Action is forbidden.';
    if (data.error.message === 'User Id cannot be empty.') return 'Action is forbidden.';
    return 'Something went wrong while fetching User. Please try again.';
  })
  .on(getUserEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(deleteUserEffect, () => null)
  .on(deleteUserEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while deleting User. Please try again.').message)
  .on(deleteUserEffect.done, () => null)
  .on(updateUserEffect, () => null)
  .on(updateUserEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while updating User. Please try again.').message)
  .on(updateUserEffect.done, () => null)
  .on(changePasswordEffect, () => null)
  .on(
    changePasswordEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while changing Password. Please try again.').message,
  )
  .on(changePasswordEffect.done, () => null)
  .on(lockUserEffect, () => null)
  .on(lockUserEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while locking User. Please try again.').message)
  .on(lockUserEffect.done, () => null)
  .on(unlockUserEffect, () => null)
  .on(
    unlockUserEffect.fail,
    (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while unlocking User. Please try again.').message,
  )
  .on(unlockUserEffect.done, () => null)
  .on(deleteCardEffect, () => null)
  .on(deleteCardEffect.fail, (_, data) => ApiUtils.getApiErrorData(data.error, 'Something went wrong while removing Card. Please try again.').message)
  .on(deleteCardEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUserApi = ApiUtils.wrapperApi(getUserEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUserApi.cancel());

export const $userStore = {
  events: {
    getUser: getUserApi.trigger,
    updateUser: updateUserEffect,
    changePassword: changePasswordEffect,
    lockUser: lockUserEffect,
    unlockUser: unlockUserEffect,
    deleteUser: deleteUserEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
